/**
 * Registry + matrix singletons for the web client. Same approach as the worker:
 * imports pack/matrix JSON statically so Vite bundles them.
 */
import {
  filterRelationTypesByActiveExtensions as filterByExtensions,
  MatrixValidator,
  type Pack,
  RegistryRuntime,
  validRelationTypeIds,
} from "@ae/domain";
import analyticsPack from "../../../../registry/analytics-pack.json";
import archimateMatrix from "../../../../registry/archimate-matrix.json";
import archimatePack from "../../../../registry/archimate-pack.json";
import archimate31Pack from "../../../../registry/archimate31-pack.json";
import bmcPack from "../../../../registry/bmc-pack.json";
import c4Matrix from "../../../../registry/c4-matrix.json";
import c4Pack from "../../../../registry/c4-pack.json";
import channelPack from "../../../../registry/channel-pack.json";
import drawingMatrix from "../../../../registry/drawing-matrix.json";
import drawingPack from "../../../../registry/drawing-pack.json";
import folderPack from "../../../../registry/folder-pack.json";
import implementationPack from "../../../../registry/implementation-pack.json";
import metaPack from "../../../../registry/meta-pack.json";
import metricPack from "../../../../registry/metric-pack.json";
import motivationPack from "../../../../registry/motivation-pack.json";
import operationalPack from "../../../../registry/operational-pack.json";
import riskPack from "../../../../registry/risk-pack.json";
import roadmapMatrix from "../../../../registry/roadmap-matrix.json";
import roadmapPack from "../../../../registry/roadmap-pack.json";
import sharpPack from "../../../../registry/sharp-pack.json";
import sharpWorkflowPack from "../../../../registry/sharp-workflow-pack.json";

let registry: RegistryRuntime | null = null;
let matrix: MatrixValidator | null = null;

export function getRegistry(): RegistryRuntime {
  if (!registry) {
    registry = new RegistryRuntime();
    registry.registerPack(archimatePack as unknown as Pack);
    // Direct ná de base-pack: de compat-stereotypes hangen aan de generieke
    // v4-types die daar gedefinieerd zijn (ArchiMate 4-migratie fase 2).
    registry.registerPack(archimate31Pack as unknown as Pack);
    registry.registerPack(c4Pack as unknown as Pack);
    registry.registerPack(drawingPack as unknown as Pack);
    registry.registerPack(sharpPack as unknown as Pack);
    registry.registerPack(sharpWorkflowPack as unknown as Pack);
    registry.registerPack(riskPack as unknown as Pack);
    registry.registerPack(operationalPack as unknown as Pack);
    registry.registerPack(motivationPack as unknown as Pack);
    registry.registerPack(metricPack as unknown as Pack);
    registry.registerPack(implementationPack as unknown as Pack);
    registry.registerPack(channelPack as unknown as Pack);
    registry.registerPack(bmcPack as unknown as Pack);
    registry.registerPack(folderPack as unknown as Pack);
    registry.registerPack(analyticsPack as unknown as Pack);
    registry.registerPack(roadmapPack as unknown as Pack);
    registry.registerPack(metaPack as unknown as Pack);
  }
  return registry;
}

export function getMatrix(): MatrixValidator {
  if (!matrix) {
    // Pack-matrices merged in dependency-order. C4 adds rules for
    // `c4.DeployedOn` / `c4.Includes` plus appends to `archimate.Flow`.
    // Drawing adds two matrix-free relations (`drawing.Line`,
    // `drawing.ArrowLine`) without touching ArchiMate rules.
    matrix = new MatrixValidator([archimateMatrix, c4Matrix, drawingMatrix, roadmapMatrix]);
  }
  return matrix;
}

/**
 * Returns the relation type-ids that the matrix considers valid between the
 * given source and target concept types, restricted to the active set.
 */
export function validRelationTypesBetween(sourceTypeId: string, targetTypeId: string): string[] {
  return validRelationTypeIds(getRegistry(), getMatrix(), sourceTypeId, targetTypeId);
}

/**
 * Returns relation type-ids valid when source and target are swapped.
 * Includes types that are also valid in the forward direction — the picker
 * shows them as both "Type" and "Type (omgekeerd)" so the user can flip
 * the arrow without changing relation type.
 */
export function validRelationTypesReversed(sourceTypeId: string, targetTypeId: string): string[] {
  return validRelationTypesBetween(targetTypeId, sourceTypeId);
}

/** Extensie-bewuste filter; de regel zelf staat in `@ae/domain` (`relationFilter.ts`). */
export function filterRelationTypesByActiveExtensions(
  typeIds: readonly string[],
  sourceStereotypes: readonly string[] | undefined,
  targetStereotypes: readonly string[] | undefined,
): string[] {
  return filterByExtensions(getRegistry(), typeIds, sourceStereotypes, targetStereotypes);
}

/** Resolve a bilingual text to a single string with fallback chain. */
export function resolveBilingual(
  text: Record<string, string>,
  preferredLang: string,
  fallbackLang = "nl",
): string {
  // Lege/whitespace-only waarden tellen als "afwezig" zodat de fallback-keten
  // dóórloopt: `??` zou een lege string (bv. een gewiste EN-naam) teruggeven
  // i.p.v. terug te vallen op de andere taal → node toonde dan "(zonder naam)".
  const nonEmpty = (v: string | undefined): v is string =>
    typeof v === "string" && v.trim().length > 0;
  if (nonEmpty(text[preferredLang])) return text[preferredLang];
  if (nonEmpty(text[fallbackLang])) return text[fallbackLang];
  return Object.values(text).find(nonEmpty) ?? "(unnamed)";
}

/**
 * Toon-naam van een concept (fase 2 Sharp-glossary). Voor `sharp.Attribute`
 * en `sharp.Identifier` met een `attribute_type_ref` wordt de naam LIVE
 * afgeleid van het gerefereerde `sharp.AttributeType` — zo komt een hernoeming
 * van het gedeelde attribuuttype overal mee (compartment-regel, node-label,
 * panel) zonder kopie/cascade. Resolutie loopt over de (al branch-overlay-
 * resolved) store, dus respecteert plateau-branches automatisch. Valt terug
 * op de eigen `name` wanneer er geen referentie is of het doel ontbreekt.
 */
export function resolveConceptName(
  concept: {
    typeId: string;
    name: Record<string, string>;
    attributes?: Record<string, unknown>;
  },
  getConcept: (id: string) => { name: Record<string, string> } | undefined,
  preferredLang: string,
  fallbackLang = "nl",
): string {
  if (concept.typeId === "sharp.Attribute" || concept.typeId === "sharp.Identifier") {
    const ref = concept.attributes?.attribute_type_ref;
    if (typeof ref === "string" && ref.length > 0) {
      const target = getConcept(ref);
      if (target) return resolveBilingual(target.name, preferredLang, fallbackLang);
    }
  }
  return resolveBilingual(concept.name, preferredLang, fallbackLang);
}

/**
 * **Label-resolver per viewpoint** (M5-§3). Kiest welke label
 * getoond moet worden voor een concept of relatie op een view-kind.
 *
 * Werking:
 * 1. View-kind heeft een `label_resolver` (bv. "sharp" of "c4")?
 *    Pak `altLabels[resolver]` als die bestaat en niet leeg is
 * 2. Anders / fallback: gebruik de standaard `name`/`label`
 * 3. Bij beide: `resolveBilingual` doet de taal-fallback-keten
 *
 * Concrete use-cases:
 * - Sharp Concept Model View (`label_resolver: "sharp"`) toont
 *   `sharpLabel: "Klant"` i.p.v. ArchiMate-naam (mogelijk
 *   technische repr. zoals "Customer-record")
 * - C4 Container View (`label_resolver: "c4"`) toont
 *   `c4Label: "Customer Service"` i.p.v. de officiële
 *   applicatie-naam uit de service catalog
 * - Standaard ArchiMate-views (geen `label_resolver`) tonen `name`
 *
 * Bilingual-fallback gaat via `resolveBilingual` (preferredLang →
 * fallbackLang → eerste niet-lege taal → "(unnamed)").
 */
export function resolveLabelForViewKind(
  // De source kan zowel een Concept als een Relation zijn — beide
  // hebben `name`/`label` + `altLabels`. Genericized via dit
  // discriminerende type.
  source:
    | { name: Record<string, string>; altLabels: Record<string, Record<string, string>> }
    | { label: Record<string, string>; altLabels: Record<string, Record<string, string>> },
  viewKindId: string | null | undefined,
  preferredLang: string,
  fallbackLang = "nl",
): string {
  // Default-tekst: name (Concept) of label (Relation). Welke aanwezig is.
  const defaultText = "name" in source ? source.name : source.label;
  if (!viewKindId) return resolveBilingual(defaultText, preferredLang, fallbackLang);
  const view = getRegistry().getViewKind(viewKindId);
  const resolver = view?.label_resolver;
  if (!resolver) return resolveBilingual(defaultText, preferredLang, fallbackLang);
  // Per-taal: het alt-label geldt alleen als de waarde vóór de getoonde taal
  // niet leeg is. Anders valt 't terug op de gewone naam/label (in díe taal).
  // Zo betekent "EN-alt wissen" dat een EN-view weer de gewone EN-naam toont,
  // i.p.v. de (nog gevulde) NL-alt of een lege string.
  const altForLang = source.altLabels[resolver]?.[preferredLang];
  if (altForLang && altForLang.trim().length > 0) {
    return altForLang;
  }
  return resolveBilingual(defaultText, preferredLang, fallbackLang);
}

/**
 * De distinct `label_resolver`-sleutels (bv. `"sharp"`, `"c4"`, `"bmc"`)
 * die relevant zijn voor een element-type — d.w.z. er bestaat minstens
 * één actief viewpoint met die resolver waarop dit type is toegestaan
 * (M8 §5, optie A). Gebruikt door het AttributePanel om alleen zinvolle
 * alt-label-invoervelden te tonen: een BusinessObject met «Concept»
 * krijgt "sharp", een ApplicationComponent "c4", enz. — geen ruis van
 * resolvers waar 't type nooit onder valt. Volgorde = eerste-voorkomen
 * over de viewpoint-lijst (stabiel).
 */
export function relevantResolversForType(typeId: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const vk of getRegistry().listActiveViewKinds()) {
    const resolver = vk.label_resolver;
    if (!resolver || seen.has(resolver)) continue;
    const allowed = allowedTypeIdsForViewKind(vk.id);
    // `null` = free-form viewpoint (alles toegestaan) → type is eligible.
    if (allowed === null || allowed.has(typeId)) {
      seen.add(resolver);
      out.push(resolver);
    }
  }
  return out;
}

/**
 * Directe sub-stereotypes van een gegeven stereotype die op het opgegeven
 * base-type mogen landen — bv. `subStereotypesForBase("c4.Container",
 * "archimate.ApplicationComponent")` geeft Microservice / WebApp / MobileApp /
 * … terug (maar niet Database, dat alleen op SystemSoftware past). Gebruikt
 * door de sub-stereotype-keuzedialoog bij «Container»-creatie (M8).
 */
export function subStereotypesForBase(
  parentStereotypeId: string,
  baseTypeId: string,
): Array<{ id: string; label: Record<string, string> }> {
  const reg = getRegistry();
  return reg
    .listStereotypes()
    .filter(
      (st) =>
        st.extends_stereotype === parentStereotypeId &&
        // Bewust `appliesToTypeIds` (condities eraf) i.p.v.
        // `canApplyStereotype`: dat laatste zou óók de subtype-keten
        // meewegen, en dat is een gedragsverbreding die hier niet thuishoort.
        reg.appliesToTypeIds(st).includes(baseTypeId),
    )
    .map((st) => ({ id: st.id, label: st.label }));
}

/**
 * Relatie-variant van {@link relevantResolversForType}. Een relatie-type
 * is eligible voor een resolver als er een actief viewpoint met die
 * resolver bestaat dat het relatie-type toestaat — via
 * `included_relation_types`, of free-form (geen lijst = alles toegestaan).
 */
export function relevantResolversForRelationType(relationTypeId: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const vk of getRegistry().listActiveViewKinds()) {
    const resolver = vk.label_resolver;
    if (!resolver || seen.has(resolver)) continue;
    const inc = vk.included_relation_types;
    const allowed = !inc || inc.length === 0 || inc.includes(relationTypeId);
    if (allowed) {
      seen.add(resolver);
      out.push(resolver);
    }
  }
  return out;
}

/**
 * Tells whether an element type is allowed on a diagram of the given
 * view-kind. The view-kind's `included_types` (allowlist) and
 * `visible_layers` / `visible_aspects` are honoured. When NEITHER is set
 * (e.g. `archimate.anything`), every element type is allowed.
 *
 * Returns `true` when the view-kind id is unknown, so that legacy or
 * unknown viewpoints don't accidentally block the user from working.
 */
export function isElementTypeAllowedOnViewKind(typeId: string, viewKindId: string): boolean {
  const reg = getRegistry();
  const view = reg.getViewKind(viewKindId);
  if (!view) return true;
  const elementType = reg.getElementType(typeId);
  if (!elementType) return false;
  // Drawing primitives staan boven de view-kind-restricties: een
  // rechthoek of lijn is niet-semantisch en hoort op elk diagram-type
  // toepasbaar te zijn (bv. een boundary-box op een Capability Map).
  if (isDrawingType(elementType)) return true;
  // Composite-laag types (Grouping, Location, SecurityDomain,
  // AND/OR-junction) zijn cross-laag connectors / groupers — ook
  // altijd toegestaan, ongeacht view-kind-restricties.
  if (elementType.layer === "composite") return true;
  const hasInclude = (view.included_types?.length ?? 0) > 0;
  const hasLayer = (view.visible_layers?.length ?? 0) > 0;
  const hasAspect = (view.visible_aspects?.length ?? 0) > 0;
  if (!hasInclude && !hasLayer && !hasAspect) return true; // free-form
  // Subtype-bewust: een workspace-subtype telt mee als z'n parent-type in
  // `included_types`/`excluded_types` staat (een subtype IS-EEN parent).
  const effTypes = reg.effectiveTypeIdsFor(typeId);
  if (hasInclude && view.included_types?.some((t) => effTypes.has(t))) return true;
  if (hasLayer && view.visible_layers?.includes(elementType.layer)) return true;
  if (hasAspect && view.visible_aspects?.includes(elementType.aspect)) return true;
  if (view.excluded_types?.some((t) => effTypes.has(t))) return false;
  return false;
}

/**
 * Voor view-kinds met `auto_apply_stereotype`: lever het stereotype-id
 * dat aan een geplaatst concept toegevoegd moet worden, mits het
 * concept's typeId in de `applies_to`-lijst staat. Returnt `undefined`
 * wanneer de view-kind geen auto-apply-regel heeft of het type buiten
 * scope valt.
 */
export function autoApplyStereotypeFor(viewKindId: string, typeId: string): string | undefined {
  const reg = getRegistry();
  const view = reg.getViewKind(viewKindId);
  const rule = view?.auto_apply_stereotype;
  if (!rule) return undefined;
  // Subtype-bewust: pas de auto-stereotype ook toe als een parent-type van
  // dit (mogelijke) subtype in `applies_to` staat.
  const effTypes = reg.effectiveTypeIdsFor(typeId);
  return rule.applies_to.some((t) => effTypes.has(t)) ? rule.stereotype_id : undefined;
}

/** Hex-mapping voor de status_indicator-kleur-tokens. Centraal zodat
 *  alle status-bolletjes consistent renderen op de canvas. */
const STATUS_INDICATOR_HEX: Record<"green" | "yellow" | "orange" | "red", string> = {
  green: "#22c55e",
  yellow: "#eab308",
  orange: "#f59e0b",
  red: "#dc2626",
};

/**
 * Voor een concept op een view-kind: bepaal of er een status-bolletje
 * gerenderd moet worden, en zo ja met welke kleur. Doorloopt het concept's
 * stereotypes, vindt de eerste met een `status_indicator`-config, kijkt
 * of de referenced attribute-waarde een gemapte kleur heeft, en returnt
 * de hex-code + duiding voor screen-readers. Returnt `null` wanneer de
 * view-kind de feature niet aan heeft staan, geen stereotype een rule
 * heeft, of het attribuut leeg/niet-gemapt is.
 */
export function statusIndicatorFor(
  viewKindId: string,
  concept: {
    stereotypes?: readonly string[];
    attributes?: Record<string, unknown>;
  },
): { color: string; label: string } | null {
  const reg = getRegistry();
  const view = reg.getViewKind(viewKindId);
  if (!view?.show_stereotype_status_indicators) return null;
  for (const stereoId of concept.stereotypes ?? []) {
    const stereo = reg.getStereotype(stereoId);
    const rule = stereo?.status_indicator;
    if (!rule) continue;
    const value = concept.attributes?.[rule.attribute];
    if (typeof value !== "string" || value.length === 0) continue;
    const token = rule.colors[value];
    if (!token) continue;
    return {
      color: STATUS_INDICATOR_HEX[token],
      label: `${stereoId}.${rule.attribute} = ${value}`,
    };
  }
  return null;
}

/** All element type ids allowed on a given view kind, expanded from the view's rules. */
export function allowedTypeIdsForViewKind(viewKindId: string): Set<string> | null {
  const reg = getRegistry();
  const view = reg.getViewKind(viewKindId);
  if (!view) return null;
  const hasInclude = (view.included_types?.length ?? 0) > 0;
  const hasLayer = (view.visible_layers?.length ?? 0) > 0;
  const hasAspect = (view.visible_aspects?.length ?? 0) > 0;
  if (!hasInclude && !hasLayer && !hasAspect) return null; // free-form
  const out = new Set<string>(view.included_types ?? []);
  for (const t of reg.listActiveElementTypes()) {
    if (hasLayer && view.visible_layers?.includes(t.layer)) out.add(t.id);
    if (hasAspect && view.visible_aspects?.includes(t.aspect)) out.add(t.id);
    // Subtype-bewust: een subtype waarvan de parent in `included_types` staat
    // hoort er ook bij (palette + drop op een type-gerestricteerde view).
    if (hasInclude) {
      const eff = reg.effectiveTypeIdsFor(t.id);
      if (view.included_types?.some((x) => eff.has(x))) out.add(t.id);
    }
    // Drawing-primitieven horen op elk diagram (zelfde rationale als in
    // `isElementTypeAllowedOnViewKind`): voeg ze altijd toe aan de
    // allowlist, ook al staan ze niet in `included_types`.
    if (isDrawingType(t)) out.add(t.id);
    // Composite-laag elementen (Grouping, Location, SecurityDomain,
    // AND/OR-junction) zijn cross-laag: ArchiMate-3.0+ staat ze op
    // elke view toe. Dus zelfde altijd-toegestaan-regel als
    // drawing-primitieven.
    if (t.layer === "composite") out.add(t.id);
  }
  for (const ex of view.excluded_types ?? []) out.delete(ex);
  return out;
}

/**
 * Returns the relation type-ids die op elk diagram zijn toegestaan
 * ongeacht `included_relation_types`. Voor nu: de twee drawing-relaties
 * (`drawing.Line`, `drawing.ArrowLine`) — niet-semantische pijl/lijn die
 * je overal moet kunnen tekenen.
 */
export function alwaysAllowedRelationTypeIds(): string[] {
  return ["drawing.Line", "drawing.ArrowLine"];
}

function isDrawingType(t: { id: string; layer: string }): boolean {
  return t.layer === "drawing" || t.id.startsWith("drawing.");
}
